














import { Component, Prop, Vue } from "vue-property-decorator";
import BN from "bn.js";
import { fromWei, toWei, toBN, unitMap } from "@/lib/web3x-es/utils";

@Component
export default class InputNumeric extends Vue {
  @Prop(Object) private value!: BN;
  @Prop(Boolean) private inputDisabled!: boolean;

  @Prop(Object) private min!: BN;
  @Prop(Object) private max!: BN;

  @Prop(String) private weiUnit!: string;

  get computedInputValue(): string {
    return fromWei(this.value.toString(), this.castedWeiUnit);
  }

  set computedInputValue(newVal: string) {
    if (newVal !== "") {
      const decimals = newVal.split(".")[1] || "";
      const diff = decimals.length - 18;
      if (diff > 0) {
        newVal = newVal.slice(0, -diff);
      }

      const valToWei = toWei(newVal, this.castedWeiUnit);
      this.setInputValue(toBN(valToWei) || toBN("0"));
    } else {
      this.setInputValue(toBN("0"));
    }
  }

  get maxFromWei(): string {
    return this.max ? fromWei(this.max.toString(), this.castedWeiUnit) : "";
  }

  get castedWeiUnit() {
    return this.weiUnit as keyof typeof unitMap;
  }

  private setInputValue(newVal: BN) {
    if (!newVal || newVal.lt(this.min)) {
      newVal = this.min;
    } else if (this.max && newVal.gte(this.max)) {
      newVal = this.max;
    }

    const hasDecimals = newVal.toString().substr(-18) !== "000000000000000000";

    if (!newVal.eq(this.value) || hasDecimals) {
      this.$emit("set-value", newVal);
    }
  }

  private validateKeyDown(event: KeyboardEvent, target: any) {
    const keyCode = event.keyCode ? event.keyCode : event.which;

    // Validate that it's not exceeding the decimal limit and it's not trying to decrease the value
    // Also, if it's already the max and it's writing a number, prevent it
    const hasReachedMax = this.max && this.value.eq(this.max);
    if (
      hasReachedMax &&
      ![37, 38, 39, 40, 8, 9].includes(keyCode) // arrow keys, backspace, tab
    ) {
      event.preventDefault();
    }
    // End validation

    if (
      (keyCode < 48 || keyCode > 57) && // numeric
      (keyCode < 96 || keyCode > 105) && // numeric pad
      ![110, 190, 37, 38, 39, 40, 8, 9].includes(keyCode) // decimal point, dot, arrow keys, backspace, tab
    ) {
      event.preventDefault();
    }

    if ([38, 40].includes(keyCode)) {
      event.preventDefault();
      return keyCode === 40 ? this.decreaseValue() : this.increaseValue();
    }

    if (
      ([110, 190].includes(keyCode) && target.value.includes(".")) ||
      ([110, 190].includes(keyCode) && target.value.length === 0)
    ) {
      event.preventDefault();
    }

    // only allow dot or decimal if 0 is the entered value
    if (String(target.value) === "0" && ![110, 190].includes(keyCode)) {
      target.value = "";
    }
  }

  private getIncreaseParams(value: number) {
    const step = Math.pow(10, 1 / 3);
    const magnitude = value
      ? -Math.floor(Math.log(value) / Math.log(10) + 1)
      : 0;

    return {
      step,
      magnitude,
      stepTwo: Math.pow(step, 2),
      stepThree: Math.pow(step, 3),
      startStep: 1 / Math.pow(10, magnitude + 1),
    };
  }

  private increaseValue() {
    const valueAsNumber = Number(this.computedInputValue);
    const params = this.getIncreaseParams(valueAsNumber);

    const arrayValues = [
      params.startStep,
      Math.round(params.step) * params.startStep,
      Math.round(params.stepTwo) * params.startStep,
      Math.round(params.stepThree) * params.startStep,
    ];

    for (const val of arrayValues) {
      if (valueAsNumber < val) {
        const newValToWei = toBN(toWei(val.toString(), this.castedWeiUnit));
        this.setInputValue(newValToWei);
        break;
      }
    }
  }

  private decreaseValue() {
    const valueAsNumber = Number(this.computedInputValue);
    const params = this.getIncreaseParams(valueAsNumber);

    if (params.startStep === valueAsNumber) {
      params.startStep /= 10;
    }

    const arrayValues = [
      Math.round(params.stepThree) * params.startStep,
      Math.round(params.stepTwo) * params.startStep,
      Math.round(params.step) * params.startStep,
      params.startStep,
    ];

    if (valueAsNumber <= 0.1) {
      this.setInputValue(toBN("0"));
      return;
    }

    for (const val of arrayValues) {
      if (valueAsNumber > val) {
        const newValToWei = toBN(toWei(val.toString(), this.castedWeiUnit));
        this.setInputValue(newValToWei);
        break;
      }
    }
  }
}
